"use client";

import { PageLayout } from "@/components/ui/page-layout";
import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";

type Props = {
  title: string;
  effectiveDate: string;
  lastUpdated: string;
  html: string;
};

function LegalBody({ title, effectiveDate, lastUpdated, html }: Props) {
  return (
    <>
      <h1 className="text-ds-text-primary mb-4 text-4xl font-bold md:text-5xl">{title}</h1>
      <p className="text-ds-text-muted mb-12 text-sm">
        <strong>NPC Labs, Inc.</strong>
        <br />
        <strong>Effective Date:</strong> {effectiveDate} <span className="mx-1">|</span> <strong>Last Updated:</strong>{" "}
        {lastUpdated}
      </p>

      <div className="text-ds-text-secondary text-base leading-relaxed">
        <div className="ghost-content" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </>
  );
}

export function LegalPage(props: Props) {
  const isAuthenticated = useIsAuthenticated();

  if (isAuthenticated) {
    return (
      <PageLayout>
        <div className="mx-auto max-w-4xl">
          <LegalBody {...props} />
        </div>
      </PageLayout>
    );
  }

  return (
    <div className="bg-ds-surface-primary min-h-screen">
      <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8 lg:py-16 xl:px-12">
        <LegalBody {...props} />
      </div>
    </div>
  );
}

/**
 * Emergency fallback rendered when Ghost CMS is unreachable. Legal pages are
 * compliance-critical so we never render an empty shell — we show a short
 * stub that directs users to contact legal@ for the current policy.
 */
export function LegalPageUnavailable({ title }: { title: string }) {
  return (
    <LegalPage
      title={title}
      effectiveDate="—"
      lastUpdated="—"
      html={`
        <p>This policy is temporarily unavailable because our content service is unreachable.</p>
        <p>For the current version of this document, please contact
          <a href="mailto:legal@npclabs.org">legal@npclabs.org</a>.
          Our team will respond within one business day.
        </p>
        <p>Your use of B3OS remains governed by the most recent version of this policy that was in effect.</p>
      `}
    />
  );
}
